import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import type { JSX, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { AuthHost } from "../auth-errors";
import { initializeAuthentication } from "../initialize-authentication";
import { syncActiveAccount } from "../sync-active-account";

export type { AuthHost } from "../auth-errors";

type InitializerState = "initializing" | "guest" | "ready" | "error";

type AccountWithUser<UserType> = AccountInfo & {
	readonly user: UserType | null;
};

export type AppAuthMode = "guest" | "ready";

type AppInitializerContextValue<UserType> = {
	readonly msal: IPublicClientApplication;
	readonly account: AccountWithUser<UserType> | null;
	readonly user: UserType | null;
	readonly authMode: AppAuthMode;
	readonly host: AuthHost;
	/** After Teams authenticate() writes MSAL cache, promote guest -> ready. */
	readonly promoteFromGuest: () => Promise<void>;
};

type AppInitializerProps<UserType> = {
	readonly children: ReactNode;
	readonly configuredScopes: string | undefined;
	readonly getUser?: (args: {
		readonly msal: IPublicClientApplication;
		readonly account: AccountInfo;
	}) => Promise<UserType> | UserType;
	readonly resolveHost?: () => Promise<AuthHost>;
	readonly getTeamsLoginHint?: () => Promise<string | null>;
	/** Teams tab SSO. Returns an account the MSAL cache does not know about. */
	readonly resolveHostAccount?: () => Promise<AccountInfo | null>;
	readonly onGuest?: (host: AuthHost) => void;
	readonly onReady?: (host: AuthHost) => void;
	readonly onBeforeInteractive?: (host: AuthHost) => void;
	readonly error?: JSX.Element;
	readonly initializing?: JSX.Element;
	readonly onError?: (error: Error) => void;
	readonly handleAuthStateChange?: (status: InteractionStatus) => void;
};

const AppInitializerContext = createContext<AppInitializerContextValue<unknown> | null>(null);

export const useAppAuth = <UserType,>(): AppInitializerContextValue<UserType> => {
	const context = useContext(AppInitializerContext);

	if (!context) {
		throw new Error("useAppAuth must be used within an AppInitializer.");
	}

	return context as AppInitializerContextValue<UserType>;
};

export const AppInitializer = <UserType,>({
	children,
	configuredScopes,
	getUser,
	resolveHost,
	getTeamsLoginHint,
	resolveHostAccount,
	onGuest,
	onReady,
	onBeforeInteractive,
	error,
	initializing,
	onError,
	handleAuthStateChange,
}: AppInitializerProps<UserType>): JSX.Element | undefined => {
	const { inProgress, instance } = useMsal();
	const [state, setState] = useState<InitializerState>("initializing");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [contextValue, setContextValue] = useState<AppInitializerContextValue<UserType> | null>(null);
	const hostRef = useRef<AuthHost>("web");
	const stateRef = useRef<InitializerState>("initializing");
	const didStartInit = useRef(false);
	const didStartWebRedirect = useRef(false);
	const getUserRef = useRef(getUser);
	getUserRef.current = getUser;
	stateRef.current = state;

	const buildReadyContext = useCallback(
		async (host: AuthHost, hostAccount?: AccountInfo | null): Promise<AppInitializerContextValue<UserType>> => {
			const activeAccount = hostAccount ?? (syncActiveAccount(instance) ? instance.getActiveAccount() : null);
			if (!activeAccount) {
				throw new Error("Authenticated account could not be resolved.");
			}

			const resolveUser = getUserRef.current;
			const user = resolveUser
				? await resolveUser({
						msal: instance,
						account: activeAccount,
					})
				: null;

			const accountWithUser: AccountWithUser<UserType> = {
				...activeAccount,
				user,
			};

			return {
				msal: instance,
				account: accountWithUser,
				user,
				authMode: "ready",
				host,
				promoteFromGuest: async () => undefined,
			};
		},
		[instance]
	);

	const promoteFromGuestRef = useRef<() => Promise<void>>(async () => undefined);

	const promoteFromGuest = useCallback(async (): Promise<void> => {
		const host = hostRef.current;
		const ready = await buildReadyContext(host);
		setContextValue({
			...ready,
			promoteFromGuest: () => promoteFromGuestRef.current(),
		});
		setState("ready");
		onReady?.(host);
	}, [buildReadyContext, onReady]);
	promoteFromGuestRef.current = promoteFromGuest;

	useEffect(() => {
		if (inProgress !== InteractionStatus.None) {
			return;
		}

		handleAuthStateChange?.(inProgress);

		if (didStartInit.current) {
			return;
		}
		didStartInit.current = true;

		let isMounted = true;

		const initializeApplication = async (): Promise<void> => {
			try {
				const host = resolveHost ? await resolveHost() : "web";
				hostRef.current = host;

				const loginHint = host === "teams" && getTeamsLoginHint ? await getTeamsLoginHint() : null;

				if (didStartWebRedirect.current && !syncActiveAccount(instance)) {
					return;
				}

				const outcome = await initializeAuthentication({
					instance,
					configuredScopes: configuredScopes ?? "",
					host,
					loginHint,
					resolveHostAccount,
					onBeforeInteractive: () => {
						onBeforeInteractive?.(host);
					},
				});

				if (!isMounted) {
					return;
				}

				if (outcome.kind === "redirecting") {
					didStartWebRedirect.current = true;
					return;
				}

				if (outcome.kind === "guest") {
					setContextValue({
						msal: instance,
						account: null,
						user: null,
						authMode: "guest",
						host,
						promoteFromGuest,
					});
					setState("guest");
					onGuest?.(host);
					return;
				}

				const ready = await buildReadyContext(host, outcome.account);
				if (!isMounted) {
					return;
				}
				setContextValue({
					...ready,
					promoteFromGuest,
				});
				setState("ready");
				onReady?.(host);
			} catch (error: unknown) {
				if (!isMounted) {
					return;
				}

				const resolvedErrorMessage = error instanceof Error ? error.message : "Authentication initialization failed.";

				setErrorMessage(resolvedErrorMessage);
				setState("error");
				onError?.(error as Error);
			}
		};

		void initializeApplication();

		return () => {
			isMounted = false;
			if (stateRef.current === "initializing" && !didStartWebRedirect.current) {
				didStartInit.current = false;
			}
		};
	}, [
		buildReadyContext,
		configuredScopes,
		getTeamsLoginHint,
		handleAuthStateChange,
		inProgress,
		instance,
		onBeforeInteractive,
		onError,
		onGuest,
		onReady,
		promoteFromGuest,
		resolveHost,
		resolveHostAccount,
	]);

	if (state === "error") {
		console.error(errorMessage, {
			config: {
				scopes: configuredScopes,
				account: instance.getActiveAccount(),
			},
		});
		return error;
	}

	if (state === "initializing") {
		return initializing;
	}

	if (!contextValue) {
		return error;
	}

	return (
		<AppInitializerContext.Provider value={contextValue as AppInitializerContextValue<unknown>}>
			{children as JSX.Element}
		</AppInitializerContext.Provider>
	);
};
